// PaperVerse Writer — Layer 2 repair handoff templates.
//
// When Layer 0 (deterministic fixers) and Layer 1 (one-shot LLM repair)
// cannot resolve a compile failure, the status banner shows a templated
// message keyed on the top remaining diagnostic. Zero tokens per
// exhaustion message — the model never writes user-facing error copy.
//
// See .lovable/plan.md — "Layer 2 — Templated handoff (zero tokens)".

import type {
  CompileDiagnostic,
  CompileDiagnosticCode,
} from "./compileDiagnostics";

export type HandoffCtaId =
  | "add_citation"
  | "edit_section"
  | "add_package"
  | "check_math"
  | "check_braces"
  | "generate_figure"
  | "ask_agent"
  | "show_log";

export interface HandoffCta {
  id: HandoffCtaId;
  /** Short button label. */
  label: string;
  /** Whether this is the recommended primary action. */
  primary: boolean;
  /**
   * Chat prompt to prefill when the CTA fires. Absent for meta CTAs
   * (`ask_agent`, `show_log`) that do not deep-link into the composer.
   * See `src/lib/writer/deepLink.ts`.
   */
  prompt?: string;
}

export interface RepairHandoff {
  code: CompileDiagnosticCode;
  /** One-line summary the banner headlines. */
  headline: string;
  /** One-sentence explanation of the likely cause. */
  cause: string;
  /** Ordered CTAs — primary first. `ask_agent` and `show_log` are always appended. */
  ctas: HandoffCta[];
}

const ASK_AGENT: HandoffCta = { id: 'ask_agent', label: 'Ask agent to try again', primary: false };
const SHOW_LOG: HandoffCta = { id: 'show_log', label: 'Show details', primary: false };

/** Format a diagnostic target for inclusion in a prefilled prompt. */
const t = (target?: string) => (target ? ` "${target}"` : '');


/**
 * Complete template map — one entry per `CompileDiagnosticCode`. The
 * `handoffTemplateExhaustiveness` test below asserts we never miss a
 * code, so adding a new diagnostic code is a compile-time forcing
 * function to add a template here.
 */
const TEMPLATES: Record<
  CompileDiagnosticCode,
  (target?: string) => Omit<RepairHandoff, "code">
> = {
  undefined_citation: (target) => ({
    headline: target
      ? `Missing citation "${target}"`
      : 'A citation is undefined',
    cause:
      "The bibliography has no entry for this key. Add it, or replace the \\cite with an existing key.",
    ctas: [
      {
        id: 'add_citation',
        label: 'Add citation',
        primary: true,
        prompt: `Compile failed: \\cite${t(target)} has no matching bibliography entry. Search literature for a plausible reference and add it to references.bib using the same key, then re-compile.`,
      },
      {
        id: 'edit_section',
        label: 'Edit in place',
        primary: false,
        prompt: `Compile failed: \\cite${t(target)} is undefined. Locate the section that uses this citation and either replace it with an existing bibliography key or remove the \\cite call, then re-compile.`,
      },
    ],
  }),
  missing_bib_entry: (target) => ({
    headline: target
      ? `Bibliography is missing "${target}"`
      : 'Bibliography is missing an entry',
    cause:
      'BibTeX ran but could not find this key in references.bib. Adding it will resolve the citation.',
    ctas: [
      {
        id: 'add_citation',
        label: 'Add citation',
        primary: true,
        prompt: `BibTeX is missing the entry${t(target)}. Use search_literature to find the paper and add it to references.bib with this exact key, then re-compile.`,
      },
    ],
  }),
  undefined_reference: (target) => ({
    headline: target
      ? `Reference "${target}" is undefined`
      : 'A cross-reference is undefined',
    cause:
      'No \\label{...} in the document matches this \\ref. Either add the label to the target section/figure or fix the ref.',
    ctas: [
      {
        id: 'edit_section',
        label: 'Edit section',
        primary: true,
        prompt: `Compile failed: \\ref${t(target)} has no matching \\label. Add the label to the intended section/figure/equation, or update the \\ref to an existing label, then re-compile.`,
      },
    ],
  }),
  undefined_control_sequence: (target) => ({
    headline: target
      ? `Undefined command ${target}`
      : 'An undefined command was used',
    cause:
      'The command is not defined by any loaded package. The right \\usepackage may be missing.',
    ctas: [
      {
        id: 'add_package',
        label: 'Add package',
        primary: true,
        prompt: `Compile failed: undefined control sequence${t(target)}. Identify the LaTeX package that defines this command and add it to the preamble via \\usepackage{...}, then re-compile.`,
      },
      {
        id: 'edit_section',
        label: 'Edit section',
        primary: false,
        prompt: `Compile failed: undefined control sequence${t(target)}. Locate the usage and either replace it with an equivalent command already available, or remove it, then re-compile.`,
      },
    ],
  }),
  missing_dollar: () => ({
    headline: 'Math mode was not opened or closed',
    cause:
      'LaTeX expected a `$` — usually an unmatched math delimiter or a math symbol used outside math mode.',
    ctas: [
      {
        id: 'check_math',
        label: 'Check math delimiters',
        primary: true,
        prompt:
          'Compile failed with a "missing $ inserted" error. Scan the document for unmatched math delimiters ($...$, \\(...\\), \\[...\\]) or math symbols used outside math mode, fix them, then re-compile.',
      },
    ],
  }),
  runaway_argument: () => ({
    headline: 'Runaway argument — unbalanced braces',
    cause:
      'A command opened `{` without a matching `}`, and LaTeX ran off the end of the argument.',
    ctas: [
      {
        id: 'check_braces',
        label: 'Check braces',
        primary: true,
        prompt:
          'Compile failed with a "runaway argument" error. Find the command whose `{` is not matched by a closing `}`, balance the braces, then re-compile.',
      },
    ],
  }),
  file_not_found: (target) => ({
    headline: target
      ? `File not found: ${target}`
      : 'A referenced file is missing',
    cause:
      'The compile bundle is assembled from the document itself — there is no external asset upload, so \\includegraphics / \\input of a standalone file can never resolve. Draw the figure inline or drop the reference.',
    ctas: [
      {
        id: 'generate_figure',
        label: 'Draw figure inline',
        primary: true,
        prompt: `Compile failed because${t(target)} is not in the project bundle, and this project has no external asset storage. Replace the \\includegraphics with an inline figure drawn in TikZ/pgfplots (use the plot tools if the figure is data-driven), then re-compile.`,
      },
      {
        id: 'edit_section',
        label: 'Remove reference',
        primary: false,
        prompt: `Compile failed because${t(target)} is not in the project bundle. Remove the \\input or \\includegraphics that references it (or comment it out), then re-compile.`,
      },
    ],
  }),
  tikz_error: (target) => ({
    headline: target
      ? `A diagram uses a style that isn't available: ${target}`
      : "A diagram uses a style that isn't available",
    cause:
      'A TikZ figure uses an arrow tip, shape or option from a drawing library that is not loaded, or one that does not exist.',
    ctas: [
      {
        id: 'edit_section',
        label: 'Fix the diagram',
        primary: true,
        prompt: `Compile failed in a TikZ diagram: the style${t(target)} is not available. Replace it with a standard equivalent (for arrows use -Stealth or -Latex from arrows.meta; for positioning use "right=of" from positioning) or load the matching \\usetikzlibrary, then re-compile.`,
      },
    ],
  }),
  figure_placeholder: (target) => ({
    headline: 'A figure shows a placeholder',
    cause: `The diagram could not be drawn${target ? ` (${target})` : ''}, so the PDF shows a framed note in its place. The rest of the paper is up to date.`,
    ctas: [
      {
        id: 'edit_section',
        label: 'Fix the figure',
        primary: true,
        prompt: `A TikZ figure could not be drawn${t(target)} and shows a placeholder in the PDF. Fix the diagram using only standard options from loaded libraries, then re-compile.`,
      },
    ],
  }),
  latex_error: (target) => ({
    headline: 'LaTeX raised an error',
    cause: target
      ? `The build stopped at: ${target}`
      : "The build stopped on an error we don't recognise yet. Show details has the full log.",
    ctas: [
      {
        id: 'edit_section',
        label: 'Edit source',
        primary: true,
        prompt:
          'Compile failed with an uncategorised LaTeX error. Read the latest compile log via the tools available, locate the failing section, propose a fix, and re-compile.',
      },
    ],
  }),
  bibtex_error: () => ({
    headline: 'BibTeX raised an error',
    cause:
      'The bibliography file has a syntax error near a specific line. Full log points at the entry.',
    ctas: [
      {
        id: 'add_citation',
        label: 'Edit bibliography',
        primary: true,
        prompt:
          'Compile failed with a BibTeX syntax error. Inspect references.bib, fix the malformed entry the log points at, then re-compile.',
      },
    ],
  }),
};


/**
 * Pick the "top" diagnostic to handoff on. Errors beat warnings; within
 * a severity, the first-in-list wins (parseCompileLog already orders by
 * source position).
 */
export function pickTopDiagnostic(
  diagnostics: readonly CompileDiagnostic[],
): CompileDiagnostic | null {
  if (diagnostics.length === 0) return null;
  const errors = diagnostics.filter((d) => d.severity === "error");
  if (errors.length > 0) return errors[0];
  return diagnostics[0];
}

/**
 * Build the handoff object for the current compile-failed state.
 * Returns null when there are no parsed diagnostics — the caller should
 * fall back to a generic "compile failed, see log" banner.
 */
export function buildRepairHandoff(
  diagnostics: readonly CompileDiagnostic[],
): RepairHandoff | null {
  const top = pickTopDiagnostic(diagnostics);
  if (!top) return null;
  const build = TEMPLATES[top.code];
  // The generic template has no target of its own; show the real error line.
  const filled = build(top.code === 'latex_error' ? top.target ?? top.message : top.target);
  return {
    code: top.code,
    headline: filled.headline,
    cause: filled.cause,
    ctas: [...filled.ctas, ASK_AGENT, SHOW_LOG],
  };
}

/** Exposed for the exhaustiveness test. */
export const REPAIR_HANDOFF_CODES = Object.keys(TEMPLATES) as CompileDiagnosticCode[];
